// ENG-2499: the appointment a Digital Form on Appointment Forms prints for, shared by single print and Bulk Print.
// The objects both mounts already hold have no signature or license, so it reads its own.
import { useMemo } from "react";

import { appointmentFormValues } from "components/word-processor-field/merge-tags/merge-tags-values/appointment-form-values.util";
import { useGetAppointmentQuery } from "services/appointments";

import { useFormClinic } from "./use-form-clinic.hook";

// The merge tags' appointment query (`getMergeTagsAppointment`), with the clinic's id and the provider's NPI.
export const APPOINTMENT_FORM_TAGS_QUERY =
  "{id,patient_feedback_rating,outcome,outcome_reason,outcome_notes,start_moment,end_moment,status,companion_name,companion_type,notes,physician_referral,referral_source{id,name},sub_referral_source{id,name},referring_physician,staff_member{id,first_name,last_name,suffix,title,is_active,signature,email,npi,license_number,resolved_license_number},room{name,clinic{id,name,logo,practice{id,name,logo}}},clinic{id,name,timezone,practice{id,name,logo}},type{id,default_duration,name,is_opportunity,general_type},chart_note,zoom_join_url,supervising_provider{id,first_name,last_name,title,suffix,signature},is_direct_supervisor}";

type UseAppointmentFormContextParams = {
  appointmentId?: number;
  /** A Digital Form is picked, on a surface whose flags are on. Off, nothing is asked for. */
  enabled: boolean;
};

/**
 * `boundAppointment` and the values are only ever this appointment's: `currentData` clears while a new id loads, where
 * `data` would keep filling from the last one. It reads afresh on mount, so a provider changed since an earlier read
 * doesn't print. `isUnavailable` says the appointment or its clinic can't be read, so the form mustn't open: its Clinic
 * tags would otherwise print the top-bar clinic.
 */
export const useAppointmentFormContext = ({
  appointmentId,
  enabled
}: UseAppointmentFormContextParams) => {
  const skip = !enabled || !appointmentId;
  const { currentData, isFetching } = useGetAppointmentQuery(
    { id: appointmentId ?? 0, query: APPOINTMENT_FORM_TAGS_QUERY },
    { skip, refetchOnMountOrArgChange: true }
  );
  const boundAppointment =
    !skip && currentData?.id === appointmentId ? currentData : undefined;

  const clinicId = boundAppointment?.clinic?.id;
  const { formClinic, isLoading: isClinicLoading } = useFormClinic({
    clinicId,
    enabled: Boolean(boundAppointment)
  });
  const isLoading = (!skip && isFetching) || isClinicLoading;

  const additionalMergeTagsValues = useMemo(
    () =>
      boundAppointment && {
        appointment: appointmentFormValues(boundAppointment),
        ...(formClinic && { formClinic })
      },
    [boundAppointment, formClinic]
  );

  return {
    boundAppointment,
    additionalMergeTagsValues,
    isLoading,
    isUnavailable:
      !skip &&
      !isLoading &&
      (!boundAppointment || (Boolean(clinicId) && !formClinic))
  };
};
